<script setup lang="ts">
import { computed, ref } from 'vue';
import StructuredAddressForm from '@/components/session/kyc/address/StructuredAddressForm.vue';
import Loading from '@/components/ui/Loading.vue';

import { storeToRefs } from 'pinia';
import { useKycSessionStore } from '@/stores/kycSession.store';
import countries from 'i18n-iso-countries';
import { Address } from '@/models/Address';
import UnverifiableUnstructuredAddressForm from '@/components/session/kyc/address/UnverifiableUnstructuredAddressForm.vue';
import VerifiableUnstructuredAddressFormLegacy from '@/components/session/kyc/address/VerifiableUnstructuredAddressFormLegacy.vue';
import GoogleMapsApiLoader from '@/components/auxiliary/GoogleMapsApiLoader.vue';
import { config } from '@/config';
import VerifiableUnstructuredAddressForm from './VerifiableUnstructuredAddressForm.vue';
import { AddressValidationAvailableRegionCodes, type RegionCode } from '@/models/Region';

// Only for Sumsub testing
function getRegionOverride(regionCode: string) {
  switch (regionCode) {
    case 'AQ':
      return 'NZ';
    default:
      return regionCode;
  }
}

const isLoading = ref(true);
const kycSessionStore = useKycSessionStore();
const { kycSession } = storeToRefs(kycSessionStore);

if (!kycSession?.value?.region) {
  throw new Error('Kyc Region not found');
}

const regionOverride = getRegionOverride(kycSession.value.region);

const countryName = countries.getName(regionOverride, 'en');

if (!countryName) {
  throw new Error('Country not found');
}

const countryOverride = countryName;

const currentAddress = ref<Address>(Address.create());

const currentCountry = computed(() => currentAddress.value.countryCode || kycSession.value?.region);

const isAddressCountryVerifiable = computed(() =>
  AddressValidationAvailableRegionCodes.some((c) => currentCountry.value === c)
);

const isManualMode = ref(false);

const verifiableAddress = computed(() => currentCountry.value && isAddressCountryVerifiable.value);

async function saveAddress() {
  if (!currentAddress.value) {
    throw new Error('No address found');
  }

  const isStructuredAddressRequired =
    isManualMode.value ||
    kycSession.value?.isStructuredAddressRequired(currentCountry.value as RegionCode);

  const residentialAddress = Address.toResidentialAddress({
    address: currentAddress.value,
    addressFormat: isStructuredAddressRequired ? 'structured' : 'unstructured',
  });

  await kycSession.value?.cardholder.saveResidentialAddress(residentialAddress);
}

/**Event Handlers */
function toggleAddressFormMode() {
  isManualMode.value = !isManualMode.value;
}

/**End Event Handlers */

async function loadAddress(): Promise<Address> {
  let kycResidentialAddress: Address = Address.create();

  //TEMPORARY: Not autopupulating addresses for BGW users
  if (kycSession.value?.skipAutoPopulation()) {
    return kycResidentialAddress;
  }

  const currentResidentialAddress =
    await kycSession.value?.cardholder.loadCurrentResidentialAddress();
  if (currentResidentialAddress?.country && currentResidentialAddress.country === regionOverride) {
    kycResidentialAddress = Address.forResidentialAddress(currentResidentialAddress);
  }
  // ignore if the saved address does not match the kyc session region, so the user can enter a new address
  return kycResidentialAddress;
}

let loadedAddress = ref<string>();

async function init() {
  const address = await loadAddress();
  currentAddress.value = address;
  loadedAddress.value = address.fullAddress;

  isLoading.value = false;
}

init();
</script>

<template>
  <Loading class="mx-auto mt-10 h-10 w-10" v-if="isLoading" />
  <div v-else>
    <UnverifiableUnstructuredAddressForm
      v-if="!verifiableAddress && currentAddress?.addressType === 'unstructured'"
      v-model="currentAddress"
      :save-address="saveAddress"
      :loadedAddress="loadedAddress"
    />
    <GoogleMapsApiLoader v-else>
      <VerifiableUnstructuredAddressForm
        v-if="verifiableAddress && !isManualMode && config.googlePlacesApiNewEnabled"
        v-model="currentAddress"
        :switch-mode="toggleAddressFormMode"
        :region-override="regionOverride"
        :save-address="saveAddress"
      />
      <VerifiableUnstructuredAddressFormLegacy
        v-else-if="verifiableAddress && !isManualMode"
        v-model="currentAddress"
        :switch-mode="toggleAddressFormMode"
        :region-override="regionOverride"
        :save-address="saveAddress"
      />
      <StructuredAddressForm
        v-else
        v-model="currentAddress"
        :switch-mode="toggleAddressFormMode"
        :region-override="regionOverride"
        :country-override="countryOverride"
        :save-address="saveAddress"
        :is-manual-mode-only="!isAddressCountryVerifiable"
      />
    </GoogleMapsApiLoader>
  </div>
</template>
